"use client";

/**
 * Modal card for resolving stale org-profile values.
 *
 * Extracted from `compass-intake.jsx:995-1058` (`.cs-stalemodal`).
 */

import { useEffect, useRef, useState } from "react";
import { ArrowR, X } from "../lib/icons";

export interface StaleModalValue {
  label: string;
  oldVal: string;
  newVal: string;
  by: string;
  when: string;
}

export interface StaleModalCardProps {
  values: StaleModalValue[];
  onUpdate?: (index: number) => void;
  onPublish?: (index: number, value: string) => void;
  onKeep?: (index: number) => void;
  onClose?: () => void;
  embedded?: boolean;
}

export function StaleModalCard({
  values,
  onUpdate,
  onPublish,
  onKeep,
  onClose,
  embedded = false,
}: StaleModalCardProps) {
  const [idx, setIdx] = useState(0);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const editInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing) editInputRef.current?.focus();
  }, [editing]);
  const [statuses, setStatuses] = useState<string[]>(() =>
    values.map(() => "pending"),
  );

  const total = values.length;
  const resolvedCount = statuses.filter((s) => s !== "pending").length;

  function advance() {
    setEditing(false);
    setDraft("");
    setIdx((i) => i + 1);
  }

  function resolve(status: string, val?: string) {
    setStatuses((s) => s.map((v, j) => (j === idx ? status : v)));
    if (status === "updated") onUpdate?.(idx);
    else if (status === "published" && val) onPublish?.(idx, val);
    else if (status === "kept") onKeep?.(idx);
    advance();
  }

  /* Done screen */
  if (idx >= total) {
    const updated = statuses.filter(
      (s) => s === "updated" || s === "published",
    ).length;
    return (
      <div
        className={[
          "w-[540px] max-w-full bg-(--bg-white) rounded-(--radius-container) overflow-hidden",
          embedded
            ? "shadow-(--shadow-sm) border border-(--border-subtle)"
            : "shadow-[0_20px_50px_-12px_rgba(0,0,0,.35)]",
        ].join(" ")}
      >
        <div className="flex gap-3 items-start p-[18px_18px_14px]">
          <div className="flex-1 min-w-0">
            <div className="text-[15.5px] font-bold text-(--fg-primary) tracking-[-0.01em] leading-[1.35]">
              Updates reviewed
            </div>
            <div className="text-[12.5px] leading-[1.5] text-(--fg-secondary) mt-[5px]">
              {updated} of {total} updated
              {resolvedCount < total ? `, ${total - resolvedCount} left` : ""}.
              You can revisit any value from its source in the rail.
            </div>
          </div>
          {onClose && (
            <button
              type="button"
              className="shrink-0 ml-auto bg-transparent border-0 text-(--fg-muted) cursor-pointer p-[5px] rounded-(--radius-xs) flex hover:text-(--fg-primary) hover:bg-(--bg-hover)"
              onClick={onClose}
              title="Close"
            >
              <X size={18} />
            </button>
          )}
        </div>
        <div className="flex items-center gap-[10px] p-[15px_18px_18px]">
          <button
            type="button"
            className="text-[13px] font-semibold bg-(--brand-navy) text-white border-0 rounded-(--radius-field) px-[14px] py-[9px] cursor-pointer hover:bg-(--steel-110)"
            onClick={onClose}
          >
            Done
          </button>
        </div>
      </div>
    );
  }

  const cur = values[idx] as StaleModalValue;

  return (
    <div
      className={[
        "w-[540px] max-w-full bg-(--bg-white) rounded-(--radius-container) overflow-hidden",
        embedded
          ? "shadow-(--shadow-sm) border border-(--border-subtle)"
          : "shadow-[0_20px_50px_-12px_rgba(0,0,0,.35)]",
      ].join(" ")}
    >
      {/* Header */}
      <div className="flex items-start gap-3 p-[18px_18px_12px]">
        <div>
          <div className="text-[15.5px] font-bold text-(--fg-primary) tracking-[-0.01em]">
            Org profile updates
          </div>
          <div className="text-[12px] text-(--fg-muted) mt-[3px]">
            {idx + 1} of {total} since this opportunity was created
          </div>
        </div>
        {onClose && (
          <button
            type="button"
            className="shrink-0 ml-auto bg-transparent border-0 text-(--fg-muted) cursor-pointer p-[5px] rounded-(--radius-xs) flex hover:text-(--fg-primary) hover:bg-(--bg-hover)"
            onClick={onClose}
            title="Close"
          >
            <X size={18} />
          </button>
        )}
      </div>

      {/* Progress dots */}
      <div className="flex gap-1.5 px-[18px] pb-1">
        {values.map((_, j) => (
          <span
            key={j}
            className={[
              "w-[22px] h-1 rounded-sm",
              j === idx
                ? "bg-(--brand-navy)"
                : statuses[j] !== "pending"
                  ? "bg-(--prov)"
                  : "bg-(--bg-hover)",
            ].join(" ")}
          />
        ))}
      </div>

      {/* Detail */}
      <div className="p-[18px]">
        <div className="text-[13px] text-(--fg-secondary) font-medium">
          {cur.label}
        </div>
        <div className="flex items-center gap-3 mt-[10px] text-[30px] font-bold tracking-[-0.02em]">
          <span className="text-(--fg-subtle) line-through decoration-2">
            {cur.oldVal}
          </span>
          <span className="text-(--stale)">
            <ArrowR size={18} />
          </span>
          <span className="text-(--fg-primary)">{cur.newVal}</span>
        </div>
        <div className="text-[12.5px] leading-[1.5] text-(--fg-muted) mt-3">
          Updated on your org profile by {cur.by} · {cur.when}. This opportunity
          still uses {cur.oldVal}.
        </div>

        {editing ? (
          <div className="mt-4">
            <div className="text-[11px] tracking-[.08em] uppercase text-(--fg-muted) font-semibold mb-2">
              Publish a value to the org profile
            </div>
            <input
              type="text"
              className="w-full text-[18px] font-bold text-(--fg-primary) bg-(--bg-white) border border-(--border-default) rounded-(--radius-sm) px-[11px] py-[10px] outline-none focus:border-(--fg-brand) focus:shadow-[inset_0_0_0_1px_var(--fg-brand)]"
              ref={editInputRef}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && draft.trim())
                  resolve("published", draft);
              }}
            />
            <div className="flex gap-[9px] mt-4">
              <button
                type="button"
                className="text-[13px] font-semibold bg-(--brand-navy) text-white border-0 rounded-(--radius-field) px-[14px] py-[9px] cursor-pointer hover:bg-(--steel-110) disabled:bg-(--border-default) disabled:text-(--fg-muted)"
                disabled={!draft.trim()}
                onClick={() => resolve("published", draft)}
              >
                Publish to org profile
              </button>
              <button
                type="button"
                className="text-[13px] font-semibold bg-(--bg-white) text-(--fg-secondary) border border-(--border-default) rounded-(--radius-field) px-[14px] py-[9px] cursor-pointer hover:bg-(--bg-canvas)"
                onClick={() => setEditing(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-start gap-[10px] mt-4">
            <button
              type="button"
              className="w-full justify-center text-[13px] font-semibold bg-(--brand-navy) text-white border-0 rounded-(--radius-field) px-[14px] py-[9px] cursor-pointer hover:bg-(--steel-110) inline-flex items-center gap-2"
              onClick={() => resolve("updated")}
            >
              Use new value · {cur.newVal}
            </button>
            <button
              type="button"
              className="w-full justify-center text-[13px] font-semibold bg-(--bg-white) text-(--fg-secondary) border border-(--border-default) rounded-(--radius-field) px-[14px] py-[9px] cursor-pointer hover:bg-(--bg-canvas) inline-flex items-center gap-2"
              onClick={() => {
                setDraft(cur.newVal);
                setEditing(true);
              }}
            >
              Edit & publish to org profile
            </button>
            <button
              type="button"
              className="inline-block mt-3 bg-transparent border-0 text-[11.5px] text-(--fg-muted) underline underline-offset-2 cursor-pointer leading-[1.4] text-left p-0 font-[family-name:var(--font-body)] hover:text-(--fg-secondary)"
              onClick={() => resolve("kept")}
            >
              Keep this opportunity's value, detaching it from the org profile
              (not recommended)
            </button>
          </div>
        )}
      </div>

      {/* Footer skip */}
      {!editing && (
        <div className="p-[4px_18px_16px]">
          <button
            type="button"
            className="bg-transparent border-0 text-[12.5px] text-(--fg-brand) underline underline-offset-2 cursor-pointer font-medium font-[family-name:var(--font-body)]"
            onClick={advance}
          >
            {idx < total - 1 ? "Skip for now" : "Skip and finish"}
          </button>
        </div>
      )}
    </div>
  );
}
